import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';
import { FaPrint, FaHistory } from 'react-icons/fa';

import API from 'core/Api';

import { PermissionKeys } from 'store/types';
import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { getSubdomain } from 'utils/helpers/routing';

import { ISelectOptionProps } from 'components/form/types';
import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import { buildFilters } from 'components/table/utils/filtersConfig';
import TooltipWrapper from 'components/TooltipWrapper';
import SignDoc from 'components/documents/SignDoc';
import RejectDoc from 'components/documents/RejectDoc';
import HardDeleteDoc from 'components/documents/HardDeleteDoc';

import Spinner from 'components/Spinner';
import InnerWrapper from '../InnerWrapper';

const Screen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { showModal } = useModal();
  const { language } = useLanguageContext();
  const { checkPermission } = usePermissionsContext();
  const queryClient = useQueryClient();
  const history = useHistory();
  const isOperator = getSubdomain() === 'operators';

  const [customSelectableRowProps, setCustomSelectableRowProps] = useState<any[]>([]);
  const [multiActionActive, setMultiActionActive] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const createGroup = () => {
    const url = buildClientUrl('/health-and-safety-training/create', client);
    history.push(url);
  };

  const renderActions = (buttons: any[], rowData: any) => {
    return (
      <>
        {buttons.map((button, i) => {
          if (button.type === 'sign') {
            return (
              <SignDoc
                key={`${rowData.document_id}-sign`}
                id={rowData.document_id}
                docHolderId={rowData.document_id}
                buttonText={t('documents.table.sign')}
                handlePreview={docId => handlePreviewDoc({ showModal, id: rowData.document_id })}
                invalidate={() =>
                  queryClient.invalidateQueries(buildClientEntity('health-and-safety-training', client))
                }
                size="sm"
              />
            );
          }

          if (button.type === 'reject') {
            return (
              <RejectDoc
                ariaLabel="Reject document"
                key={`${rowData.document_id}-reject`}
                id={rowData.document_id}
                docHolderId={rowData.document_id}
                buttonText={t('documents.table.reject')}
                handlePreview={docId => handlePreviewDoc({ showModal, id: rowData.document_id })}
                invalidate={() =>
                  queryClient.invalidateQueries(buildClientEntity('health-and-safety-training', client))
                }
                size="sm"
                reason={false}
              />
            );
          }

          if (button.type === 'delete') {
            return (
              <HardDeleteDoc
                key={`${rowData.document_id}-delete`}
                id={rowData.id}
                buttonText={t('common.delete')}
                invalidate={() =>
                  queryClient.invalidateQueries(buildClientEntity('health-and-safety-training', client))
                }
                className="mr-0 ml-2"
                size="sm"
                disabled={button.disabled}
              />
            );
          }

          return null;
        })}

        <span className="d-inline-block ml-2 mt-2">
          <TooltipWrapper
            id="tooltip-print"
            overlay={t('documents.table.preview')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              outlined
              disabled={isLoadingDownload}
              onClick={() =>
                handlePreviewDoc({ showModal, id: rowData.document_id, setIsLoading: setIsLoadingDownload })
              }
            >
              {!isLoadingDownload && <FaPrint size={14} />}
              {isLoadingDownload && <Spinner size={14} />}
            </Button>
          </TooltipWrapper>
        </span>

        <div className="d-inline-block ml-2 mt-2">
          <TooltipWrapper
            id="tooltip-history"
            overlay={t('documents.table.history')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              outlined
              onClick={() => {
                history.push(buildClientUrl(`/document-activity/${rowData.document_id}`, client));
              }}
            >
              <FaHistory size={14} />
            </Button>
          </TooltipWrapper>
        </div>
      </>
    );
  };

  const columns: Array<Column<any>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('healthAndSafetyTraining.user'),
      accessor: 'user',
      Cell: ({ row }) => <>{`${row.original.user.first_name} ${row.original.user.last_name}`}</>,
    },
    {
      Header: t('healthAndSafetyTraining.group'),
      accessor: 'user.groups',
      Cell: ({ row }) => (
        <>
          {row.original.user.groups.length ? row.original.user.groups.map((group: any) => group.name).join(', ') : ''}
        </>
      ),
    },
    {
      Header: t('healthAndSafetyTraining.type'),
      accessor: 'type',
      width: '5rem',
      Cell: ({ row }) => <>{t(`healthAndSafetyTraining.types.${row.original.type}`)}</>,
    },
    {
      Header: t('healthAndSafetyTraining.documentNumber'),
      accessor: 'document_id',
      width: '5rem',
      Cell: ({ row }) => <>{row.original.document_id || ''}</>,
    },
    {
      Header: t('healthAndSafetyTraining.date'),
      accessor: 'facilitated_on',
      width: '5rem',
      Cell: ({ row }) => (
        <>{row.original.facilitated_on ? formatDate(new Date(row.original.facilitated_on), 'dd.mm.YYY') : ''}</>
      ),
    },
    {
      Header: t('healthAndSafetyTraining.status'),
      accessor: 'status',
      Cell: ({ row }) => <>{t(`healthAndSafetyTraining.statuses.${row.original.status}`)}</>,
    },
    {
      Header: () => <div className="text-right">{t('healthAndSafetyTraining.actions')}</div>,
      accessor: 'actions',
      width: '9rem',
      Cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>{renderActions(row.original.buttons || [], row.original)}</div>
      ),
    },
  ];

  const {
    isFetching,
    isLoading: isLoadingDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('health-and-safety-training-dropdowns', client)],
    async (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      const res = await API.get('health-and-safety-training/dropdown-options');
      return res.data && res.data[language || 'bg'];
    },
    {
      cacheTime: 0,
    }
  );

  const tableFilters = useMemo(() => {
    if (!dropdowns) return null;

    const statusWithDefaults = dropdowns.status?.map(opt => {
      if (opt.value === 'signed_by_employer' || opt.value === 'created') {
        return { ...opt, selected: true };
      }
      return opt;
    });

    return [
      buildFilters({
        name: 'facilitated_on_gte',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'facilitated_on_lte',
        placeholder: t('leaves.dateTo'),
        ariaLabel: 'Leave date end',
        type: 'monthyearpickerend',
        subType: 'leavesWaitingApproval',
        setLastMonth: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('healthAndSafetyTraining.user'),
        options: dropdowns?.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'type',
        placeholder: t('healthAndSafetyTraining.type'),
        options: dropdowns?.type,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'facilitator',
        placeholder: t('healthAndSafetyTraining.facilitator'),
        options: dropdowns?.facilitator,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'status',
        placeholder: t('healthAndSafetyTraining.status'),
        options: statusWithDefaults,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [dropdowns, t]);

  return (
    <InnerWrapper
      canMultiSign={checkPermission(PermissionKeys.CAN_MULTI_SIGN)}
      canMultiDelete={checkPermission(PermissionKeys.CAN_MULTI_DELETE)}
      customSelectableProps={customSelectableRowProps}
      setMultiActionActive={setMultiActionActive}
      setCustomSelectableRowProps={setCustomSelectableRowProps}
    >
      {tableFilters && (
        <FetchTable
          entityName={buildClientEntity('health-and-safety-training', client)}
          entityEndpoint="/health-and-safety-training"
          columns={columns}
          filters={tableFilters}
          loading={isFetching || isLoadingDropdowns}
          pageLimit={20}
          pagination
          hideSearch
          isSelectable
          useCustomSelectBtn
          customSelectableRowProps={customSelectableRowProps}
          disablePagination={multiActionActive}
          {...(!isOperator && {
            onAdd: createGroup,
            addBtnLabel: t('healthAndSafetyTraining.create'),
          })}
        />
      )}
    </InnerWrapper>
  );
};

export default Screen;
